import { Link } from 'react-router-dom';
import { Warning } from '@react-apps/design-system';
import type { BillingAgreement } from '../../domain/entities/sales.entity';
import { useContract } from '../hooks/use-contract-details';
import { Field, SectionCard, formatDateTime } from '../shared/display';
import {
  formatDocumentForDisplay,
  formatPhoneForDisplay,
  formatZipCodeForDisplay,
} from '@/shared/utils/br-fields';
import { Tooltip } from '@/shared/components/tooltip';

/**
 * "O gateway não mandou este campo" — distinto do `---` que o `Field` usa para
 * vazio genérico. A diferença importa aqui: na Hotmart, a credencial de
 * afiliado não recebe o comprador e a de coprodutor não recebe o endereço, e
 * ler isso como "não temos o dado" manda o operador procurar um erro que não
 * existe.
 */
function NotSent() {
  return (
    <span className="text-(--color-text-secondary) italic">
      O gateway não informou
    </span>
  );
}

const orNotSent = (value: string | null) =>
  value === null || value === '' ? <NotSent /> : value;

/**
 * "É outra coisa?" e não "é outra string?": `De Araújo` × `de Araujo` é a mesma
 * pessoa escrita por dois formulários, e marcar isso como divergência treina o
 * operador a ignorar o aviso. Por isso nome ignora caixa, acento e espaço;
 * e-mail, só caixa; documento, só dígitos.
 */
const sameName = (a: string, b: string) =>
  a
    .trim()
    .replace(/\s+/g, ' ')
    .localeCompare(b.trim().replace(/\s+/g, ' '), 'pt-BR', {
      sensitivity: 'base',
    }) === 0;
const sameEmail = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();
const sameDoc = (a: string, b: string) =>
  a.replace(/\D/g, '') === b.replace(/\D/g, '');

/**
 * O valor do checkout com o do cadastro atual ao lado, só quando divergem.
 * Campo que o gateway não mandou não diverge de nada — não há o que comparar.
 */
function WithHolder({
  checkout,
  display,
  holder,
  holderDisplay = holder,
  same,
}: {
  checkout: string | null;
  display: React.ReactNode;
  holder: string | null | undefined;
  holderDisplay?: string | null;
  same: (a: string, b: string) => boolean;
}) {
  if (!checkout || !holder || same(checkout, holder)) return <>{display}</>;
  return (
    <span className="inline-flex items-center gap-1.5">
      {display}
      <Tooltip
        message={`Cadastrado no sistema como: ${holderDisplay}`}
        triggerLabel="Diverge do cadastro atual"
        triggerClassName="text-yellow-600 dark:text-yellow-500 inline-flex"
      >
        <Warning size="sm" />
      </Tooltip>
    </span>
  );
}

/**
 * O comprador **como veio do checkout**, conferido contra o titular que a nossa
 * base tem hoje. É o par que motivou o card: o cadastro é sobrescrito pela
 * compra seguinte, editado pela própria pessoa e reapontado quando o contrato
 * troca de dono — e até aqui nada guardava quem de fato comprou. O cadastro só
 * aparece onde diverge (ícone ao lado do campo); repeti-lo inteiro quando bate
 * era ruído, e escondia a divergência entre campos iguais.
 *
 * Três estados, e nenhum deles é o `---` genérico: o contato existe, o gateway
 * não mandou aquele campo, ou nunca capturamos a venda inteira (`buyerContact`
 * nulo — venda anterior ao registro e sem webhook guardado para preenchê-la).
 *
 * Leitura pura: não há botão que copie o dado para o cadastro. O que existe
 * daqui para a frente é processamento interno.
 */
export function BuyerContactCard({
  billingAgreement,
}: {
  billingAgreement: BillingAgreement;
}) {
  const contact = billingAgreement.buyerContact;
  // Mesma query que a página do contrato já dispara — o cache do react-query
  // dedupe, não dobra a request.
  const {
    data: detail,
    isLoading: loadingHolder,
    isError: holderFailed,
  } = useContract(billingAgreement.contractId);
  const user = detail?.user ?? null;

  return (
    <SectionCard
      title="Comprador no checkout"
      action={
        <Link
          to={`/contracts/${billingAgreement.contractId}/user`}
          className="text-(--color-accent) text-sm hover:underline"
        >
          Ver dados do usuário
        </Link>
      }
    >
      {!contact ? (
        <p className="text-(--color-text-secondary) text-sm italic">
          Não capturamos o comprador desta venda — ela é anterior a este
          registro e não há webhook guardado de onde preenchê-la.
        </p>
      ) : (
        <div className="space-y-4">
          <p className="text-(--color-text-secondary) text-xs">
            Como o gateway entregou no evento da venda. Não muda quando o
            cadastro é editado nem quando o contrato troca de titular.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Nome"
              value={
                <WithHolder
                  checkout={contact.name}
                  display={orNotSent(contact.name)}
                  holder={user?.name}
                  same={sameName}
                />
              }
            />
            <Field
              label="E-mail"
              value={
                <WithHolder
                  checkout={contact.email}
                  display={orNotSent(contact.email)}
                  holder={user?.email}
                  same={sameEmail}
                />
              }
            />
            <Field
              label="Documento"
              value={
                <WithHolder
                  checkout={contact.doc}
                  display={
                    contact.doc ? (
                      formatDocumentForDisplay(contact.doc)
                    ) : (
                      <NotSent />
                    )
                  }
                  holder={user?.doc}
                  holderDisplay={
                    user?.doc ? formatDocumentForDisplay(user.doc) : null
                  }
                  same={sameDoc}
                />
              }
            />
            <Field
              label="Telefone"
              value={
                contact.phone ? (
                  formatPhoneForDisplay(contact.phone)
                ) : (
                  <NotSent />
                )
              }
            />
            {/* O instante do evento no gateway, não o da gravação — é o que diz
                de qual cobrança este contato veio. */}
            <Field
              label="Capturado em"
              value={formatDateTime(contact.capturedAt)}
            />
          </div>

          {/* Bloco próprio, e não oito campos soltos no grid: o endereço é uma
              coisa só, e o card já tem campos demais. */}
          <section className="border-(--color-border) space-y-4 rounded-lg border p-4">
            <p className="text-(--color-text-secondary) text-xs font-semibold uppercase tracking-wide">
              Endereço
            </p>
            {!contact.address ? (
              <p className="text-(--color-text-secondary) text-sm italic">
                O gateway não informou o endereço.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field
                  label="Logradouro"
                  value={orNotSent(contact.address.street)}
                />
                <Field
                  label="Número"
                  value={orNotSent(contact.address.number)}
                />
                <Field
                  label="Bairro"
                  value={orNotSent(contact.address.neighborhood)}
                />
                <Field
                  label="Complemento"
                  value={orNotSent(contact.address.complement)}
                />
                <Field label="Cidade" value={orNotSent(contact.address.city)} />
                <Field
                  label="Estado"
                  value={orNotSent(contact.address.state)}
                />
                <Field
                  label="País"
                  value={orNotSent(contact.address.country)}
                />
                <Field
                  label="CEP"
                  value={
                    contact.address.zipCode ? (
                      formatZipCodeForDisplay(contact.address.zipCode)
                    ) : (
                      <NotSent />
                    )
                  }
                />
              </div>
            )}
          </section>
        </div>
      )}

      {/* Sem titular para comparar, os ícones não aparecem — e isso não pode
          ler como "tudo bate". Por isso a falha e a ausência se dizem. */}
      {contact && !loadingHolder && (holderFailed || !user) && (
        <p className="text-(--color-text-secondary) mt-4 text-sm italic">
          {holderFailed
            ? 'Não foi possível carregar o titular atual para comparar.'
            : 'Este contrato não tem titular cadastrado para comparar.'}
        </p>
      )}
    </SectionCard>
  );
}
